/* Lotto site-wide "Notify Me" (coming-soon) for products flagged custom.notify_me.
   Powers any surface that renders a [data-lnm-root] block — the PDP buy area, the
   sticky bar's CTA target, and (future) listing cards.

   Contract is identical to the Stroma landing popup: a signed App Proxy at
   /apps/lotto-commerce-backend/notify-me. GET ?productId= returns the shopper's
   existing registrations; POST {productId, handle, variantId?} registers them and
   the backend applies the customer tag notifyme-<handle> and stores the variant.
   Logged out (401): save the intent, send the shopper to login, finish it on return
   (the API is idempotent). Server status is the source of truth — the sessionStorage
   cache is only a first paint and the GET overwrites it every open.

   The saved intent is kept until the server confirms it (200), retried with
   backoff while the Shopify session cookie catches up, and retried once more
   when the tab regains focus. It expires after 15 minutes. If it still can't
   be finished the shopper is told to tap again — never a silent loss.

   data-lnm-logged-in (printed by the snippets from Liquid's `customer`) skips
   the status GET for logged-out visitors, whose GET can only answer 401. Taps
   always POST regardless: the server, not the hint, decides login.

   Listing cards (roots inside a .lnm-plp-dropdown) share ONE
   GET /notify-me/bulk?productIds=a,b,... per 50 ids instead of a GET each;
   cards added later are batched over 150ms. 401 = logged out; 400 / non-JSON /
   network / an id missing from the answer falls back to the single GET for
   those roots. The PDP root and swatch switches keep the single GET.

   Plain browser JS, no dependencies; safe to defer-load globally. */
(function () {
  'use strict';

  var API = '/apps/lotto-commerce-backend/notify-me';
  var PENDING_KEY = 'lotto:notify:pending';
  var PENDING_TTL = 15 * 60 * 1000;
  var RESUME_DELAYS = [0, 1000, 2000, 4000, 8000];
  var RESUME_FAIL_MSG = "Couldn't complete your sign-up — tap Notify Me again.";
  function regsKey(pid) { return 'lotto:notify:regs:' + pid; }

  // Set by any 200 from the API on this page: overrides a stale logged-out hint.
  var loggedIn = false;
  var loginOpened = false;
  var resuming = false;
  var returnTries = 0;

  function pidOf(root) { return root.getAttribute('data-lnm-product-id'); }
  function handleOf(root) { return root.getAttribute('data-lnm-handle'); }

  // Selected variant: an explicit attribute wins (single-variant / no-form cards);
  // otherwise read the surrounding product form's hidden id input, which the theme's
  // variant picker keeps current (the same input Add to Cart would have submitted).
  function variantOf(root) {
    // A product-level control (no selectable size — e.g. the coming-soon PLP card)
    // registers interest for the whole product. Force empty scope regardless of any
    // product form on the page, so a stray variant id can never attach to it.
    if (root.hasAttribute('data-lnm-product-level')) return '';
    var explicit = root.getAttribute('data-lnm-variant-id');
    if (explicit) return explicit;
    var form = root.closest('form') ||
      document.querySelector('product-form form, form[data-type="add-to-cart-form"]');
    if (form) {
      var input = form.querySelector('.product-variant-id') || form.querySelector('[name="id"]');
      if (input && input.value) return input.value;
    }
    return '';
  }

  function loadRegs(pid) {
    try { var r = sessionStorage.getItem(regsKey(pid)); return r ? JSON.parse(r) : null; }
    catch (e) { return null; }
  }
  // ONE record per product for THIS page view (bulk / GET / confirmed POST),
  // shared by every root showing that product — the PDP, its card, and any card
  // swatched to it — so they can never disagree. Registrations only grow, so
  // answers are unioned. Never carried to the next page (no cross-page staleness).
  var pageRegs = window.__lnmPageRegs = window.__lnmPageRegs || {};
  function saveRegs(pid, map) {
    pid = String(pid);
    var u = copyMap(pageRegs[pid]);
    for (var k in (map || {})) { if (map[k] === true) u[k] = true; }
    pageRegs[pid] = u;
    try { sessionStorage.setItem(regsKey(pid), JSON.stringify(u)); } catch (e) {}
    // Repaint every root showing this product, not just the one that changed.
    rootsFor(pid).forEach(paint);
  }
  function regsOf(root) {
    var pid = pidOf(root), shared = pageRegs[pid];
    var own = root.__lnmRegs != null ? root.__lnmRegs : (shared ? null : loadRegs(pid));
    var u = copyMap(shared);
    for (var k in (own || {})) { if (own[k] === true) u[k] = true; }
    return u;
  }
  function copyMap(map) {
    var c = {};
    for (var k in (map || {})) { if (map[k] === true) c[k] = true; }
    return c;
  }
  // A product-level registration ('' key) covers every size.
  function isRegistered(map, vid) {
    if (!map) return false;
    return map[''] === true || (!!vid && map[vid] === true);
  }

  function setMsg(root, text) {
    var m = root.querySelector('[data-lnm-msg]');
    if (m) { m.textContent = text || ''; m.hidden = !text; }
  }

  function paint(root) {
    var btn = root.querySelector('[data-lnm-btn]');
    var label = root.querySelector('[data-lnm-label]');
    if (!btn || !label) return;
    var map = regsOf(root);
    var busy = !!(root.__lnmBusy || root.__lnmResuming);
    if (isRegistered(map, variantOf(root))) {
      label.textContent = "You'll be notified";
      btn.disabled = true;
      root.classList.add('is-done');
    } else {
      label.textContent = busy ? 'One moment…' : (btn.getAttribute('data-lnm-idle') || 'Notify Me');
      btn.disabled = busy;
      root.classList.remove('is-done');
    }
    // Card size sheet (painted by the PLP block below): its rows' "You're in"
    // follows the button, so a slow POST or a login resume updates both.
    if (root.closest && root.closest('.lnm-plp-dropdown')) {
      try { root.dispatchEvent(new CustomEvent('lnm:paint', { bubbles: true })); } catch (e) {}
    }
  }

  // Registrations only grow, so a status answer is merged with the ones this
  // page has just had confirmed by a POST (a GET can race a resume's POST).
  function applyStatus(root, pid, map) {
    var mine = root.__lnmConfirmed && root.__lnmConfirmed[pid];
    if (mine) Object.keys(mine).forEach(function (k) { map[k] = true; });
    saveRegs(pid, map);
    root.__lnmRegs = map;
    // The server already has the saved intent: nothing left to resume.
    var pend = readPending();
    if (pend && String(pend.body.productId) === pid && isRegistered(map, pend.body.variantId || '')) clearPending(pend.at);
    paint(root);
  }

  // force: skip the logged-out hint (used after an in-page login).
  function fetchStatus(root, force) {
    var pid = pidOf(root);
    if (!pid) return;
    if (!force && !loggedIn && root.getAttribute('data-lnm-logged-in') === '0') {
      // Logged out per Liquid: the GET could only answer 401. Apply that
      // answer locally (no registrations) without the round trip.
      applyStatus(root, pid, {});
      return;
    }
    root.__lnmFetched = true;
    fetch(API + '?productId=' + encodeURIComponent(pid), { headers: { accept: 'application/json' } })
      .then(function (r) { return r.status === 200 ? r.json() : (r.status === 401 ? { loggedOut: true } : null); })
      .then(function (j) {
        if (!j || pidOf(root) !== pid) return;
        var map = {};
        if (!j.loggedOut && j.data) {
          loggedIn = true;
          map = regsFrom(j.data.registrations);
        }
        applyStatus(root, pid, map);
      })
      .catch(function () { /* transient — the POST path reports to the shopper */ });
  }

  /* ---- bulk status (listing cards) ---- */
  var BULK_MAX = 50;
  var known = {}; // pid -> registrations from a bulk answer on this page
  var asked = {}; // pid -> in a bulk request (in flight or answered)
  var queued = [];
  var flushTimer = null;
  var booting = false;

  function regsFrom(list) {
    var map = {};
    (list || []).forEach(function (reg) { map[reg.variantId || ''] = true; });
    return map;
  }
  function copyRegs(map) {
    var out = {};
    Object.keys(map || {}).forEach(function (k) { out[k] = map[k]; });
    return out;
  }
  function isCardRoot(root) { return !!(root.closest && root.closest('.lnm-plp-dropdown')); }
  function rootsFor(pid, cardsOnly) {
    return Array.prototype.filter.call(document.querySelectorAll('[data-lnm-root]'), function (r) {
      return pidOf(r) === pid && (!cardsOnly || isCardRoot(r));
    });
  }
  // pick(pid) -> the roots an answer paints. Ids the call can't answer go to
  // the single GET for those roots; bulk is never retried.
  function bulkChunk(ids, pick) {
    ids.forEach(function (pid) { asked[pid] = true; });
    function settle(pid, map) {
      known[pid] = map;
      saveRegs(pid, map);
      pick(pid).forEach(function (r) { r.__lnmFetched = true; applyStatus(r, pid, copyRegs(map)); });
    }
    function fail(list) {
      list.forEach(function (pid) {
        delete asked[pid];
        pick(pid).forEach(function (r) { fetchStatus(r, true); });
      });
    }
    fetch(API + '/bulk?productIds=' + ids.join(','), { headers: { accept: 'application/json' } })
      .then(function (r) { return r.status === 401 ? { loggedOut: true } : (r.status === 200 ? r.json() : null); })
      .then(function (j) {
        if (j && j.loggedOut) { ids.forEach(function (pid) { settle(pid, {}); }); return; }
        var list = j && j.data && j.data.products;
        if (!Array.isArray(list)) { fail(ids); return; }
        var byId = {}, missing = [];
        list.forEach(function (p) { if (p && p.productId != null) byId[String(p.productId)] = p; });
        loggedIn = true;
        ids.forEach(function (pid) {
          if (byId[pid]) settle(pid, regsFrom(byId[pid].registrations));
          else missing.push(pid);
        });
        fail(missing);
      }, function () { fail(ids); });
  }
  // One bulk call per 50 products when several are asked for; one product
  // keeps the single GET. force: after an in-page login (all roots).
  function statusFor(roots, force) {
    var ids = [], seen = {};
    roots.forEach(function (r) {
      var pid = pidOf(r);
      if (pid && !seen[pid]) { seen[pid] = true; ids.push(pid); }
    });
    if (ids.length < 2) { roots.forEach(function (r) { fetchStatus(r, force); }); return; }
    var pick = function (pid) { return rootsFor(pid, !force); };
    for (var i = 0; i < ids.length; i += BULK_MAX) bulkChunk(ids.slice(i, i + BULK_MAX), pick);
  }
  function flushQueue() {
    clearTimeout(flushTimer);
    flushTimer = null;
    var roots = queued;
    queued = [];
    if (roots.length) statusFor(roots, false);
  }
  // Card roots wait for the batch; anything else (PDP root, logged-out hint)
  // takes the single path exactly as before.
  function queueStatus(root) {
    var pid = pidOf(root) || '';
    if (!isCardRoot(root) || !/^\d+$/.test(pid) ||
        (!loggedIn && root.getAttribute('data-lnm-logged-in') === '0')) { fetchStatus(root); return; }
    if (known[pid]) { root.__lnmFetched = true; applyStatus(root, pid, copyRegs(known[pid])); return; }
    if (asked[pid]) return; // painted when that answer lands
    queued.push(root);
    if (!booting && !flushTimer) flushTimer = setTimeout(flushQueue, 150);
  }

  /* ---- pending intent (saved on a 401, finished after login) ---- */
  function readPending() {
    var raw;
    try { raw = localStorage.getItem(PENDING_KEY); } catch (e) { return null; }
    if (!raw) return null;
    var pend = null;
    try { pend = JSON.parse(raw); } catch (e) { /* corrupt: dropped below */ }
    if (!pend || !pend.body || !/^\d+$/.test(String(pend.body.productId || '')) ||
        !(Date.now() - Number(pend.at) <= PENDING_TTL)) {
      clearPending();
      return null;
    }
    return pend;
  }
  // at: only clear the intent this attempt read (a newer tap may have replaced it).
  function clearPending(at) {
    try {
      if (at != null) {
        var cur = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
        if (cur && cur.at !== at) return;
      }
      localStorage.removeItem(PENDING_KEY);
    } catch (e) { /* storage blocked */ }
  }
  function clearPendingFor(body) {
    var pend = readPending();
    if (pend && String(pend.body.productId) === String(body.productId) &&
        (pend.body.variantId || '') === (body.variantId || '')) clearPending(pend.at);
  }

  // KwikPass's OTP iframe is on screen (login still in progress).
  function kpOpen() {
    var f = document.getElementById('iframe-kp');
    if (!f) return false;
    var cs = getComputedStyle(f);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && f.getBoundingClientRect().height > 0;
  }

  // KwikPass on this store is isShopifySessionEnabled:false, so only Shopify's native
  // login creates the customer session the App Proxy authorises on. Route logged-out
  // shoppers there (return_url back here); resumePending() finishes on reload. If the
  // in-page KwikPass helper is present and later wired for Shopify sessions, prefer it.
  function openLogin() {
    loginOpened = true;
    if (typeof window.handleKpAndShopifyLogin === 'function') {
      window.handleKpAndShopifyLogin(window.location.pathname + window.location.search);
      return;
    }
    window.location.href = '/account/login?return_url=' +
      encodeURIComponent(window.location.pathname + window.location.search);
  }

  // POST a registration -> { kind: 'ok' | 'auth' (401) | 'error' (definitive,
  // with the server's message) | 'retry' (5xx / network), message }.
  function send(body) {
    return fetch(API, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().then(function (j) { return { status: r.status, j: j }; }); })
      .then(function (res) {
        if (res.status === 200 && res.j && res.j.data && res.j.data.registered) return { kind: 'ok' };
        if (res.status === 401) return { kind: 'auth' };
        var err = res.j && res.j.errors && res.j.errors.error && res.j.errors.error[0];
        var message = (err && err.message) || 'Something went wrong. Please try again.';
        var definitive = res.status < 500 && !!(err && err.message);
        return { kind: definitive ? 'error' : 'retry', message: message };
      })
      .catch(function () { return { kind: 'retry', message: 'Network error. Please try again.' }; });
  }

  // isResume: a background finish of a saved intent — never reopens login and
  // leaves messaging to resumePending.
  function post(root, body, isResume) {
    root.__lnmBusy = true;
    paint(root);
    return send(body).then(function (res) {
      root.__lnmBusy = false;
      if (res.kind === 'ok') {
        loggedIn = true;
        var pid = String(body.productId), vid = body.variantId || '';
        root.__lnmConfirmed = root.__lnmConfirmed || {};
        (root.__lnmConfirmed[pid] = root.__lnmConfirmed[pid] || {})[vid] = true;
        root.__lnmRegs = root.__lnmRegs || {};
        var lnmWasRegistered = isRegistered(regsOf(root), vid);
        root.__lnmRegs[vid] = true;
        saveRegs(pid, root.__lnmRegs);
        if (!isResume) clearPendingFor(body);
        if (!lnmWasRegistered) {
          // GTM conversion: a brand-new back-in-stock notify signup, server-confirmed.
          // Same event as the launch waitlist; fires only after OTP login (when needed)
          // + registration, never on the click, and only for a newly-registered variant.
          (window.dataLayer = window.dataLayer || []).push({ event: 'notify_me_success' });
        }
        // Success is conveyed by the button's "You'll be notified" state; clear any
        // prior error/login line rather than adding a redundant confirmation message.
        setMsg(root, '');
        paint(root);
        return res;
      }
      if (isResume) { paint(root); return res; }
      if (res.kind === 'auth') {
        try { localStorage.setItem(PENDING_KEY, JSON.stringify({ body: body, at: Date.now() })); }
        catch (e) { /* storage blocked: the shopper retaps after login */ }
        // The login UI is itself the prompt — no redundant message line below the CTA.
        setMsg(root, '');
        paint(root);
        openLogin();
        return res;
      }
      setMsg(root, res.message);
      paint(root);
      return res;
    });
  }

  function onClick(root) {
    if (root.__lnmBusy || root.__lnmResuming) return;
    var pid = pidOf(root), handle = handleOf(root), vid = variantOf(root);
    if (!pid || !handle) return;
    if (isRegistered(regsOf(root), vid)) return;
    var body = { productId: String(pid), handle: handle };
    if (vid) body.variantId = String(vid);
    post(root, body);
  }

  // A coming-soon card's root sits in its (closed) Notify dropdown: reopen it so
  // the shopper sees the outcome of the saved sign-up.
  function revealCard(root) {
    var d = root.closest && root.closest('.lnm-plp-dropdown');
    if (!d || d.classList.contains('active') || typeof window.lnmPlpToggle !== 'function') return;
    var c = d.closest('.plp-atc-container');
    var trigger = c && c.querySelector('.lnm-cta');
    if (trigger) window.lnmPlpToggle(trigger);
  }

  // Finish a registration saved before a login round trip. The session cookie
  // can lag the login by seconds, so retry on the given backoff (bounded, one
  // run at a time). The intent stays saved until a 200 confirms it.
  function resumePending(delays, quiet) {
    if (resuming) return;
    var pend = readPending();
    if (!pend) return;
    resuming = true;
    var pid = String(pend.body.productId);
    // Prefer the PDP buy-area root over a listing card for the same product.
    var root = null;
    Array.prototype.forEach.call(document.querySelectorAll('[data-lnm-root][data-lnm-product-id="' + pid + '"]'), function (r) {
      if (!root || (root.closest('.lnm-plp-dropdown') && !r.closest('.lnm-plp-dropdown'))) root = r;
    });
    var vid = String(pend.body.variantId || '');
    if (root && vid && root.hasAttribute('data-lnm-product-level') && root.closest('.lnm-plp-dropdown')) {
      // The card registered a picked size: restore that scope so its state paints.
      root.removeAttribute('data-lnm-product-level');
      root.setAttribute('data-lnm-variant-id', vid);
    }
    if (root) { root.__lnmResuming = true; paint(root); }
    var i = 0;
    function finish(msg) {
      resuming = false;
      if (!root) return;
      root.__lnmResuming = false;
      if (msg != null) setMsg(root, msg);
      paint(root);
    }
    (function next() {
      setTimeout(function () {
        if (root && i === 0) revealCard(root);
        var cur = readPending();
        if (!cur || String(cur.body.productId) !== pid) { finish(); return; }
        pend = cur;
        (root ? post(root, pend.body, true) : send(pend.body)).then(function (res) {
          if (res.kind === 'ok') {
            clearPending(pend.at);
            finish('');
            // Sync the rest of the shopper's registrations unless a GET already ran.
            if (root && !root.__lnmFetched) fetchStatus(root, true);
            return;
          }
          if (res.kind === 'error') { clearPending(pend.at); finish(res.message); return; }
          if (++i < delays.length) { next(); return; }
          // Still not authorised: say so. The intent stays until it expires, so
          // a later focus or retap can still finish it.
          // Only the post-reload resume may tell the shopper; focus / popup-close
          // retries stay quiet, and never while the login window is still open.
          finish(quiet || kpOpen() ? null : RESUME_FAIL_MSG);
        });
      }, delays[i]);
    })();
  }

  // Tab regains focus / becomes visible. After an in-page login (openLogin
  // without a reload) refresh status once and finish the intent on the full
  // backoff; otherwise give a still-pending intent one more try (bounded).
  function onReturn() {
    if (document.visibilityState === 'hidden' || kpOpen()) return;
    if (loginOpened) {
      loginOpened = false;
      var all = Array.prototype.filter.call(document.querySelectorAll('[data-lnm-root]'), pidOf);
      all.forEach(function (r) { delete known[pidOf(r)]; delete asked[pidOf(r)]; });
      statusFor(all, true);
      resumePending(RESUME_DELAYS, true);
      return;
    }
    if (!resuming && returnTries < 3 && readPending()) {
      returnTries++;
      resumePending([0], true);
    }
  }

  function initRoot(root) {
    if (root.__lnmInit) return;
    root.__lnmInit = true;
    var btn = root.querySelector('[data-lnm-btn]');
    if (btn) btn.addEventListener('click', function (e) { e.preventDefault(); onClick(root); });
    // Repaint "You'll be notified" when the shopper switches size. The theme's variant
    // picker updates the form id input; listen broadly so any picker style triggers it.
    var form = root.closest('form') ||
      document.querySelector('product-form form, form[data-type="add-to-cart-form"]');
    if (form) form.addEventListener('change', function () { paint(root); });
    document.addEventListener('variant:change', function () { paint(root); });
    document.addEventListener('on:variant:change', function () { paint(root); });
    paint(root);
    queueStatus(root);
  }

  // Listing grids (collection pagination / filtering) inject cards after load. initRoot
  // is idempotent, so wiring newly-added [data-lnm-root] blocks keeps the Notify button
  // live instead of dead on those cards.
  function observeNewRoots() {
    if (typeof MutationObserver === 'undefined' || !document.body) return;
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (!n || n.nodeType !== 1) continue;
          if (n.matches && n.matches('[data-lnm-root]')) initRoot(n);
          if (n.querySelectorAll) {
            Array.prototype.forEach.call(n.querySelectorAll('[data-lnm-root]'), initRoot);
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    var roots = document.querySelectorAll('[data-lnm-root]');
    booting = true;
    Array.prototype.forEach.call(roots, initRoot);
    booting = false;
    flushQueue();
    resumePending(RESUME_DELAYS);
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    observeNewRoots();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();

/* Coming-soon PLP overlay open/close. The card's bag-icon trigger toggles our OWN
   `.lnm-plp-dropdown` (not the theme's quick-view, which rendered an empty sheet).
   stopPropagation keeps the toggle from being immediately closed by the theme's /
   our own outside-click listener; clicking elsewhere or Escape closes it. Desktop
   still reveals on hover via CSS — this is only the tap/open path for the mobile sheet. */
(function () {
  'use strict';
  /* Mobile: the card's dropdown lives inside the rail's overflow-x scroller, and
     iOS Safari traps position:fixed there — the sheet rendered inside the section
     and scrolled with it. On open, move it into a body-level wrapper that keeps
     the .plp-atc-notify class (so the existing sheet CSS still applies) and put
     it back on close. Desktop keeps the in-card hover dropdown. */
  var mq = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;

  function portalClose(d) {
    if (d) d.__lnmWant = false;
    if (!d) return;
    d.classList.remove('active');
    var wrap = d.parentNode;
    if (d.__lnmHome) {
      if (d.__lnmHome.parentNode) d.__lnmHome.parentNode.insertBefore(d, d.__lnmHome);
      d.__lnmHome.remove();
      d.__lnmHome = null;
    }
    if (wrap && wrap.classList && wrap.classList.contains('lnm-plp-portal')) wrap.remove();
  }
  /* Mobile sheet uses the Stroma "Shop the model" panel design (stroma.js):
     rows read "UK 6 (EUR 40) … Coming soon", a tapped row is selected, and the
     selected size's variant is what Notify Me registers. */
  function eurFor(size) {
    var m = /(\d+(?:\.\d+)?)/.exec(size || '');
    if (!m || !/^\s*uk/i.test(size)) return '';
    var uk = parseFloat(m[1]);
    return isFinite(uk) ? '(EUR ' + Math.round(uk + 34) + ')' : '';
  }
  // Same shared per-product record as the button (see saveRegs above).
  function regsOfRoot(root) {
    var pid = root.getAttribute('data-lnm-product-id');
    var shared = window.__lnmPageRegs && window.__lnmPageRegs[pid];
    var own = root.__lnmRegs != null ? root.__lnmRegs : (shared ? null : regsFor(root));
    var u = {};
    [shared, own].forEach(function (m) { for (var k in (m || {})) { if (m[k] === true) u[k] = true; } });
    return u;
  }
  function regsFor(root) {
    try { return JSON.parse(sessionStorage.getItem('lotto:notify:regs:' + root.getAttribute('data-lnm-product-id')) || 'null'); }
    catch (e) { return null; }
  }
  function paintRows(d) {
    var root = d.querySelector('[data-lnm-root]');
    var regs = root ? regsOfRoot(root) : null;
    Array.prototype.forEach.call(d.querySelectorAll('.lnm-plp-size-row'), function (row) {
      var na = row.querySelector('.lnm-plp-size-na');
      var vid = row.getAttribute('data-vid');
      if (na) na.textContent = regs && (regs[''] === true || (vid && regs[vid] === true)) ? "You're in" : 'Coming soon';
    });
  }
  // The card's closed pill reads "You'll be notified" once any size is
  // registered (as the Stroma shop tiles do); it still opens the sheet so more
  // sizes can be added. Looked up while the sheet is in place, then cached,
  // because an open sheet is portalled away from its card.
  function paintCta(d) {
    // An open sheet is portalled to <body>; ctaOf finds its card either way.
    if (!d.__lnmCta) d.__lnmCta = ctaOf(d) || null;
    var cta = d.__lnmCta, root = d.querySelector('[data-lnm-root]');
    var label = cta && cta.querySelector('.lnm-cta__label');
    if (!label || !root) return;
    if (cta.__lnmIdle == null) cta.__lnmIdle = label.textContent;
    var regs = regsOfRoot(root), on = false;
    for (var k in (regs || {})) { if (regs[k] === true) { on = true; break; } }
    label.textContent = on ? "You'll be notified" : cta.__lnmIdle;
    cta.classList.toggle('is-registered', on);
  }
  document.addEventListener('lnm:paint', function (e) {
    var dd = e.target && e.target.closest && e.target.closest('.lnm-plp-dropdown');
    if (dd) { paintCta(dd); paintRows(dd); }
  });
  function selectRow(d, row) {
    Array.prototype.forEach.call(d.querySelectorAll('.lnm-plp-size-row'), function (r) {
      r.classList.remove('is-selected'); r.setAttribute('aria-pressed', 'false');
    });
    row.classList.add('is-selected'); row.setAttribute('aria-pressed', 'true');
    var root = d.querySelector('[data-lnm-root]'), vid = row.getAttribute('data-vid');
    // Sizes not mapped to variants yet: keep the choice and apply it once they are.
    d.__lnmPick = vid ? null : rowName(row);
    if (root && vid) {
      root.removeAttribute('data-lnm-product-level');
      root.setAttribute('data-lnm-variant-id', vid);
      document.dispatchEvent(new CustomEvent('variant:change'));
    }
  }
  function rowName(row) {
    return ((row.querySelector('.lnm-plp-size-name') || {}).textContent || '').trim().toLowerCase();
  }
  // Rows are decorated once each (rebuilt rows get decorated when built).
  function decorateRow(d, row) {
    if (row.__lnmDecorated) return;
    row.__lnmDecorated = true;
    var name = row.querySelector('.lnm-plp-size-name');
    var mark = document.createElement('span');
    mark.className = 'lnm-plp-size-mark';
    row.insertBefore(mark, row.firstChild);
    var eur = eurFor(name ? name.textContent : '');
    if (eur && name) {
      var e = document.createElement('span');
      e.className = 'lnm-plp-size-eur';
      e.textContent = eur;
      name.insertAdjacentElement('afterend', e);
    }
    row.setAttribute('role', 'button');
    row.setAttribute('tabindex', '0');
    row.setAttribute('aria-pressed', 'false');
    // iOS Safari can swallow the first tap on a non-button row (it only
    // fires hover); select on touchend, ignoring touches that were scrolls.
    var startY = null;
    row.addEventListener('touchstart', function (ev) { startY = ev.touches[0].clientY; }, { passive: true });
    row.addEventListener('touchend', function (ev) {
      var t = ev.changedTouches[0];
      if (startY !== null && Math.abs(t.clientY - startY) < 10) { ev.preventDefault(); ev.stopPropagation(); selectRow(d, row); }
      startY = null;
    });
    row.addEventListener('click', function (ev) { ev.stopPropagation(); selectRow(d, row); });
  }
  function enhance(d) {
    if (d.__lnmEnhanced) return;
    d.__lnmEnhanced = true;
    var close = d.querySelector('.lnm-plp-close');
    if (close) close.innerHTML = '<span class="lnm-plp-close-label">Close</span>';
    Array.prototype.forEach.call(d.querySelectorAll('.lnm-plp-size-row'), function (row) { decorateRow(d, row); });
    paintRows(d);
  }

  function portalOpen(d) {
    enhance(d);
    paintRows(d);
    var home = document.createComment('lnm-home');
    d.parentNode.insertBefore(home, d);
    d.__lnmHome = home;
    var wrap = document.createElement('div');
    wrap.className = 'plp-atc-notify lnm-plp-portal';
    // iOS Safari can skip 'click' on a bare backdrop; close on touchend too.
    var onBackdrop = function (e) {
      if (e.target !== wrap) return;
      if (e.type === 'touchend') e.preventDefault();
      portalClose(d);
    };
    wrap.addEventListener('touchend', onBackdrop);
    wrap.addEventListener('click', onBackdrop);
    document.body.appendChild(wrap);
    wrap.appendChild(d);
    d.classList.add('active');
  }
  function closeAll(except) {
    var open = document.querySelectorAll('.lnm-plp-dropdown.active');
    Array.prototype.forEach.call(open, function (d) { if (d !== except) portalClose(d); });
  }
  /* Colourway swatches (changeVariantImage) swap the card to a sibling product
     and write its handle/id onto the bag button, but the notify dropdown was
     rendered for the original product. Re-point it before opening: a notify-only
     colourway gets its own product id, sizes and variants; an orderable one
     opens the theme's add-to-cart quick view instead. */
  // Each colour swatch carries its own product's custom.notify_me flag
  // (data-notify-me on .product-nav-card, rendered by the card snippets).
  function isNotifyProduct(handle, container) {
    var card = container && container.closest('.product-card');
    var sw = card && card.querySelector('.product-nav-card img[data-handle="' + handle + '"]');
    var wrap = sw && sw.closest('.product-nav-card');
    var flag = wrap && wrap.getAttribute('data-notify-me');
    return flag === null || flag === undefined || flag === '' ? true : flag === 'true';
  }
  function sizeIndex(p) {
    var i = (p.options || []).findIndex(function (o) { return /size/i.test(o.name || o); });
    return i === -1 ? 1 : i;
  }
  // One /products/<handle>.js per colourway per page view, shared by the
  // swatch re-point and the sheet's size->variant mapping.
  var productJs = {};
  function getProduct(handle) {
    if (!productJs[handle]) {
      productJs[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { if (!p) delete productJs[handle]; return p; },
              function () { delete productJs[handle]; return null; });
    }
    return productJs[handle];
  }
  /* Card sheet sync. A card's sheet must always describe the colourway the
     card is SHOWING (its button's data-handle, written by the theme's swatch
     code; else the product it was rendered for). Every swatch / open asks for
     that handle; each request bumps a per-sheet generation and only the latest
     one may touch the sheet, so a slow reply for a colour the shopper already
     left can never overwrite the current one. Rows and their variant ids are
     set from the same product JSON in the same step. */
  function ensureOrig(d) {
    if (d.__lnmOrig === undefined) {
      var root = d.querySelector('[data-lnm-root]');
      d.__lnmOrig = root ? root.getAttribute('data-lnm-handle') : null;
      // The Liquid rows are a fixed UK 6–12 list (wrong for e.g. women's UK 3–8),
      // so they are never trusted: the first sync rebuilds them from the product.
      d.__lnmRowsFor = null;
    }
  }
  function ctaOf(d) {
    var home = d.__lnmHome && d.__lnmHome.parentNode;
    var c = (d.closest && d.closest('.plp-atc-container')) || (home && home.closest ? home.closest('.plp-atc-container') : null);
    return c && c.querySelector('.lnm-cta');
  }
  // What the card is showing NOW. The theme's changeVariantImage marks the
  // tapped swatch img.lotto-active and sets the wishlist button's data-handle
  // synchronously, but writes the card button's data-handle only after its own
  // fetch (late, and out of order on quick taps) — so read the sync signals.
  function shownHandle(d) {
    ensureOrig(d);
    var cta = ctaOf(d);
    var card = cta && cta.closest ? cta.closest('.product-card') : null;
    var img = card && card.querySelector('.product-nav-card img.lotto-active[data-handle]');
    var wl = card && card.querySelector('.wishlist-btn[data-handle]');
    return (img && img.getAttribute('data-handle')) || (wl && wl.getAttribute('data-handle')) ||
      (cta && cta.getAttribute('data-handle')) || d.__lnmOrig;
  }
  function statusFor(root, d, gen) {
    var pid = root.getAttribute('data-lnm-product-id');
    var pr = window.__lnmPageRegs || {};
    function apply(map) {
      if (gen !== d.__lnmGen || root.getAttribute('data-lnm-product-id') !== pid) return;
      root.__lnmRegs = map;
      document.dispatchEvent(new CustomEvent('variant:change'));
      paintRows(d);
    }
    if (pr[pid]) { var c = {}; for (var k in pr[pid]) { if (pr[pid][k] === true) c[k] = true; } apply(c); return; }
    // Logged out per Liquid: a GET could only answer 401.
    if (root.getAttribute('data-lnm-logged-in') === '0') { apply({}); return; }
    fetch('/apps/lotto-commerce-backend/notify-me?productId=' + encodeURIComponent(pid), { headers: { accept: 'application/json' } })
      .then(function (r) { return r.status === 200 ? r.json() : null; })
      .then(function (j) {
        if (!j || !j.data) return;
        var map = {};
        (j.data.registrations || []).forEach(function (reg) { map[reg.variantId || ''] = true; });
        var prev = pr[pid] || {};
        for (var pk in prev) { if (prev[pk] === true) map[pk] = true; }
        if (window.__lnmPageRegs) window.__lnmPageRegs[pid] = map;
        try { sessionStorage.setItem('lotto:notify:regs:' + pid, JSON.stringify(map)); } catch (e) {}
        var copy = {}; for (var m in map) copy[m] = true;
        apply(copy);
      })
      .catch(function () {});
  }
  function syncSheet(d, handle) {
    ensureOrig(d);
    var gen = d.__lnmGen = (d.__lnmGen || 0) + 1;
    d.__lnmSyncing = handle;
    return getProduct(handle).then(function (p) {
      if (gen !== d.__lnmGen) return; // superseded by a newer swatch / open
      d.__lnmSyncing = null;
      var root = d.querySelector('[data-lnm-root]');
      if (p && root) {
        if (root.getAttribute('data-lnm-handle') !== p.handle) {
          root.setAttribute('data-lnm-product-id', String(p.id));
          root.setAttribute('data-lnm-handle', p.handle);
          root.setAttribute('data-lnm-product-level', '');
          root.removeAttribute('data-lnm-variant-id');
          root.__lnmRegs = null;
          setMsgIn(root, '');
        }
        var ul = d.querySelector('.lnm-plp-sizes');
        if (ul && d.__lnmRowsFor !== p.handle) {
          var k = 'option' + (sizeIndex(p) + 1), seenSize = {};
          ul.innerHTML = '';
          p.variants.forEach(function (v) {
            var size = v[k] || v.title;
            if (!size || seenSize[size]) return;
            seenSize[size] = true;
            var li = document.createElement('li');
            li.className = 'lnm-plp-size-row';
            li.innerHTML = '<span class="lnm-plp-size-name"></span> <span class="lnm-plp-size-na">Coming soon</span>';
            li.firstChild.textContent = size;
            ul.appendChild(li);
            decorateRow(d, li);
          });
          d.__lnmRowsFor = p.handle;
        }
        // Size -> variant, on the rows that are in the sheet now.
        Array.prototype.forEach.call(d.querySelectorAll('.lnm-plp-size-row'), function (row) {
          var key = rowName(row);
          var v = p.variants.filter(function (x) {
            return (x.options || []).some(function (o) { return String(o).trim().toLowerCase() === key; });
          })[0];
          if (v) row.setAttribute('data-vid', String(v.id)); else row.removeAttribute('data-vid');
        });
        d.__lnmVidsFor = p.handle;
        if (d.__lnmPick) {
          var picked = Array.prototype.filter.call(d.querySelectorAll('.lnm-plp-size-row'), function (r) { return rowName(r) === d.__lnmPick; })[0];
          d.__lnmPick = null;
          if (picked) selectRow(d, picked);
        }
        statusFor(root, d, gen);
        document.dispatchEvent(new CustomEvent('variant:change'));
        paintRows(d);
      }
      if (d.__lnmWant && !d.classList.contains('active')) { d.__lnmWant = false; portalOpen(d); }
    });
  }
  function setMsgIn(root, text) {
    var m = root.querySelector('[data-lnm-msg]');
    if (m) { m.textContent = text || ''; m.hidden = !text; }
  }
  // The theme's swatch code writes the sibling product's handle onto the card
  // button: re-point that card's sheet so its pill shows that colourway.
  function onSwatch(card) {
    var container = card && card.querySelector('.plp-atc-container');
    var d = container && container.querySelector('.lnm-plp-dropdown');
    if (!d) return;
    ensureOrig(d);
    clearTimeout(card.__lnmSwatchT);
    card.__lnmSwatchT = setTimeout(function () {
      var handle = shownHandle(d);
      if (!handle || !isNotifyProduct(handle, container)) return;
      var root = d.querySelector('[data-lnm-root]');
      if (root && root.getAttribute('data-lnm-handle') === handle && !d.__lnmSyncing) return;
      syncSheet(d, handle);
    }, 120);
  }
  if (typeof MutationObserver !== 'undefined') {
    // Swatch taps: the wishlist button's data-handle (sync) and the card
    // button's (late) both change; either re-syncs, latest shown colour wins.
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        var t = m.target;
        if (!t.classList || !(t.classList.contains('lnm-cta') || t.classList.contains('wishlist-btn'))) return;
        var card = t.closest && t.closest('.product-card');
        if (card && card.querySelector('.plp-atc-notify')) onSwatch(card);
      });
    }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['data-handle'] });
  }
  window.lnmPlpToggle = function (btn, ev) {
    if (ev && ev.stopPropagation) ev.stopPropagation();
    var container = btn.closest ? btn.closest('.plp-atc-container') : null;
    var d = container ? container.querySelector('.lnm-plp-dropdown') : null;
    if (!d) return;
    var willOpen = !d.classList.contains('active');
    closeAll(d);
    if (!willOpen) { portalClose(d); return; }
    ensureOrig(d);
    var root = d.querySelector('[data-lnm-root]');
    var handle = shownHandle(d);
    if (!root || !handle) { portalOpen(d); return; }
    if (handle !== d.__lnmOrig && !isNotifyProduct(handle, container) && typeof window.openQuickView === 'function') {
      window.openQuickView(btn, handle);
      return;
    }
    var current = root.getAttribute('data-lnm-handle') === handle && d.__lnmRowsFor === handle && !d.__lnmSyncing;
    if (current) {
      // Right product already: open now; map sizes in the background if needed.
      portalOpen(d);
      if (d.__lnmVidsFor !== handle) syncSheet(d, handle);
      return;
    }
    d.__lnmWant = true;
    syncSheet(d, handle);
  };
  window.lnmPlpClose = function (el, ev) {
    if (ev && ev.stopPropagation) ev.stopPropagation();
    portalClose(el && el.closest ? el.closest('.lnm-plp-dropdown') : null);
  };
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.plp-atc-notify')) return;
    closeAll(null);
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(null); });
})();

/* KwikPass login (GoKwik, opened by Notify Me for logged-out shoppers and by the
   header account icon) shows a fixed iframe#iframe-kp and sets body
   overflow:hidden. iOS Safari ignores that for scroll locking: focusing the OTP
   inputs pops the keyboard and the page behind scrolls on every digit. While the
   iframe is visible, pin the page with position:fixed at the current scroll
   offset, and restore that exact offset when it closes. */
(function lnmKpScrollLock() {
  'use strict';
  var mq = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;
  var lockedAt = null;

  function kpVisible() {
    var f = document.getElementById('iframe-kp');
    if (!f) return false;
    var cs = getComputedStyle(f);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && f.getBoundingClientRect().height > 0;
  }
  function lock() {
    if (lockedAt !== null) return;
    lockedAt = window.scrollY || window.pageYOffset || 0;
    var b = document.body.style;
    b.position = 'fixed';
    b.top = -lockedAt + 'px';
    b.left = '0';
    b.right = '0';
    b.width = '100%';
  }
  function unlock() {
    if (lockedAt === null) return;
    var y = lockedAt;
    lockedAt = null;
    var b = document.body.style, h = document.documentElement.style;
    // The theme sets scroll-behavior:smooth on html/body, which turned this
    // restore into a visible 0→y scroll animation (right before KwikPass reloads
    // the page after OTP). Jump instantly, then hand smooth scrolling back.
    var prevH = h.scrollBehavior, prevB = b.scrollBehavior;
    h.scrollBehavior = 'auto'; b.scrollBehavior = 'auto';
    b.position = ''; b.top = ''; b.left = ''; b.right = ''; b.width = '';
    window.scrollTo({ top: y, left: 0, behavior: 'instant' });
    requestAnimationFrame(function () { h.scrollBehavior = prevH; b.scrollBehavior = prevB; });
  }
  function sync() {
    if (mq && !mq.matches) { unlock(); return; }
    if (kpVisible()) lock(); else unlock();
  }
  var watchedFrame = null;
  function watchFrame() {
    var f = document.getElementById('iframe-kp');
    if (!f || f === watchedFrame) return;
    watchedFrame = f;
    new MutationObserver(sync).observe(f, { attributes: true, attributeFilter: ['style', 'class'] });
  }
  function start() {
    if (typeof MutationObserver === 'undefined') return;
    // KwikPass inserts its wrapper under <body> once, then toggles the iframe's
    // inline style and the body's overflow on open/close. Observe exactly those
    // (not the whole subtree — carousels mutate styles every frame).
    new MutationObserver(function () { watchFrame(); sync(); }).observe(document.body, {
      childList: true, attributes: true, attributeFilter: ['style', 'class']
    });
    watchFrame();
    sync();
  }
  if (document.body) start(); else document.addEventListener('DOMContentLoaded', start);
})();

/* Stroma landing (notify-only launch): the shop module's "+ Add" and tile "+"
   become Notify Me (same buttons, same handlers in stroma.js), and on mobile a
   sticky Notify Me appears once the hero has scrolled away. The sticky button
   drives the hero CTA itself, so stroma.js's lead capture, login resume and
   "You're on the list" state stay the single source of truth. */
(function () {
  'use strict';
  var BELL = '<svg class="lnm-bell" width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6v-5a7 7 0 0 0-5.5-6.84V3.5a1.5 1.5 0 0 0-3 0v.66A7 7 0 0 0 5 11v5l-2 2v1h18v-1l-2-2Z"/></svg>';

  function relabel(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('.st-shop__add[data-st-add]:not(.lnm-st-cta)'), function (b) {
      b.classList.add('lnm-st-cta');
      b.innerHTML = BELL + '<span class="st-label">Notify Me</span>';
    });
    Array.prototype.forEach.call(scope.querySelectorAll('.st-shop__plus[data-st-tile-add]:not(.lnm-st-plus)'), function (b) {
      b.classList.add('lnm-st-plus');
      b.innerHTML = BELL + '<span class="st-label">Notify Me</span>';
    });
  }

  function sticky() {
    var hero = document.querySelector('[data-st-hero]');
    var cta = document.querySelector('[data-st-hero-notify]');
    if (!hero || !cta || !('IntersectionObserver' in window)) return;
    var heroLabel = cta.querySelector('.st-label');
    var note = document.querySelector('[data-st-hero-note]');
    var bar = document.createElement('div');
    bar.className = 'lnm-st-sticky';
    bar.innerHTML = '<button type="button" class="lnm-st-sticky__btn">' + BELL + '<span class="lnm-st-sticky__label"></span></button>';
    var btn = bar.firstChild;
    var label = btn.lastChild;
    document.body.appendChild(bar);

    function sync() {
      var done = cta.classList.contains('is-done');
      var msg = !done && note && !note.hidden ? note.textContent.trim() : '';
      label.textContent = msg || (heroLabel && heroLabel.textContent.trim()) || 'Notify Me';
      btn.classList.toggle('is-done', done);
      btn.setAttribute('aria-disabled', done ? 'true' : 'false');
    }
    sync();
    var mo = new MutationObserver(sync);
    mo.observe(cta, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true, characterData: true });
    if (note) mo.observe(note, { attributes: true, attributeFilter: ['hidden'], childList: true, characterData: true, subtree: true });

    btn.addEventListener('click', function () {
      if (cta.classList.contains('is-done')) return;
      cta.click();
    });

    // Show only after the hero has scrolled up past the top, not before it.
    new IntersectionObserver(function (entries) {
      var e = entries[0];
      bar.classList.toggle('is-shown', !e.isIntersecting && e.boundingClientRect.top < 0);
    }).observe(hero);
  }

  function start() {
    if (!document.querySelector('.st-shop, [data-st-hero]')) return;
    relabel(document);
    Array.prototype.forEach.call(document.querySelectorAll('.st-shop'), function (root) {
      new MutationObserver(function () { relabel(root); }).observe(root, { childList: true, subtree: true });
    });
    sticky();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
